// Tax Invoice Balance API functions
import api from '../../../shared/utils/api';
import { API_ENDPOINTS, API_CONFIG } from '../../../config/api';
import { getLegacyToken, setLegacyToken } from '../../../config/etaxToken';

// Global token storage
let globalToken: string | null = null;

/**
 * Set global token for API calls
 * @param token - Authentication token
 */
export const setGlobalToken = (token: string) => {
  globalToken = token;
  console.log('🔐 Global token set for TaxInvoiceBalance API');
};

/**
 * Get global token
 * @returns Current global token
 */
export const getGlobalToken = (): string | null => {
  return globalToken;
};

/**
 * Get token from localStorage
 * @returns Token from localStorage or null
 */
export const getTokenFromStorage = (): string | null => {
  // Эхлээд etax_token-г хайх (etax API-д ашиглах)
  let etaxToken = getLegacyToken();
  
  console.log('🔐 TaxToken from localStorage:', etaxToken ? 'Present' : 'Missing');
  console.log('🔐 TaxToken length:', etaxToken ? etaxToken.length : 'null');
  console.log('🔐 TaxToken preview:', etaxToken ? etaxToken.substring(0, 50) + '...' : 'null');
  console.log('🔐 localStorage check:');
  console.log('  - etax_token:', getLegacyToken() ? 'Present' : 'Missing');
  
  if (etaxToken) {
    setGlobalToken(etaxToken);
    console.log('🔐 TaxToken loaded from localStorage');
    return etaxToken;
  }
  return null;
};

/**
 * Clear token from localStorage
 */
export const clearEtaxToken = (): void => {
  // Token цэвэрлэх - etaxToken.ts-аас clearEtaxTokens() ашиглах
  localStorage.removeItem('etax_username');
  globalToken = null;
  console.log('🔐 Token cleared from localStorage');
};

/**
 * Get Etax token from backend
 * @param username - Username
 * @param password - Password
 * @returns Promise<string> - Access token
 */
export const getEtaxToken = async (username: string, password: string): Promise<string> => {
  try {
    console.log('🔐 Getting Etax token from backend...', username);
    
    const response = await api.post('/api/tax/get-etax-token', {
      username,
      password
    });
    
    if (response.success && response.data && response.data.access_token) {
      const token = response.data.access_token;
      
      // Set global token
      setGlobalToken(token);
      
      // Save to localStorage
      setLegacyToken(token);
      localStorage.setItem('etax_username', username);
      
      console.log('✅ Etax token received and saved to localStorage');
      return token;
    } else {
      console.error('❌ Failed to get Etax token:', response);
      throw new Error(response.message || 'Failed to get Etax token');
    }
  } catch (error) {
    console.error('❌ Error getting Etax token:', error);
    throw error;
  }
};

// Interface for invoice balance response
export interface InvoiceBalanceItem {
  // Primary fields from API
  actInvoiceId: number;
  pin: string; // Харилцагчийн регистр
  taxPayerName: string; // Харилцагчийн нэр
  invoiceNo: string; // Нэхэмжлэх дугаар
  taxTypeName: string; // Татварын төрөл
  year: number; // Тайлант он
  period: number; // Тайлант үе
  balance: number; // Үлдэгдэл дүн
  status: number; // Төлөв
  
  // Additional API fields
  entId: number;
  tin: string;
  entTypeName: string;
  createdDate: string;
  invoiceDate: string;
  invoiceStatus: number;
  invoiceStatusName: string;
  periodType: string;
  taxTypeId: number;
  taxTypeCode: string;
  branchId: number;
  branchName: string;
  subBranchId: number;
  subBranchName: string;
  payable: number;
  paid: number;
  creditAmount: number;
  debitAmount: number;
  balanceCredit: number | null;
  balanceDebit: number;
  
  // Legacy fields for backward compatibility
  id?: string;
  invoiceNumber?: string;
  amount?: number;
  customerName?: string;
  customerTin?: string;
}

export interface InvoiceBalanceResponse {
  success: boolean;
  data: {
    _embedded: {
      vActInvoiceBalances: InvoiceBalanceItem[];
    };
    _links: {
      first: { href: string };
      self: { href: string };
      next?: { href: string };
      last: { href: string };
    };
    page: {
      size: number;
      totalElements: number;
      totalPages: number;
      number: number;
    };
    // Legacy fields for backward compatibility
    content?: InvoiceBalanceItem[];
    totalElements?: number;
    totalPages?: number;
  };
  message?: string;
  size?: number;
  number?: number;
  first?: boolean;
  last?: boolean;
}

// Interface for filter parameters
export interface InvoiceBalanceFilter {
  invoiceType?: number;
  balanceOperator?: '<' | '>=' | '=' | '!=';
  balanceValue?: number;
  page?: number;
  size?: number;
  sort?: string;
  entId?: string;
}

/**
 * Get invoice balance with negative balance (баланс сөрөг)
 * @param filter - Filter parameters
 * @returns Promise<InvoiceBalanceResponse>
 */
export const getNegativeInvoiceBalance = async (filter: InvoiceBalanceFilter = {}, entId?: string): Promise<InvoiceBalanceResponse> => {
  try {
    console.log('🔍 Fetching negative invoice balance...', filter);
    
    // Get token from localStorage if globalToken is not available
    if (!globalToken) {
      const tokenFromStorage = getLegacyToken();
      if (tokenFromStorage) {
        setGlobalToken(tokenFromStorage);
        console.log('🔑 Token loaded from localStorage');
      } else {
        console.log('⚠️ No token available in localStorage');
      }
    }
    
    console.log('🔑 Global token status:', globalToken ? 'Present' : 'Missing');
    
    const params = new URLSearchParams();
    
    // Default filters for negative balance
    params.append('filter', 'invoiceType,=,1');
    params.append('filter', 'balance,<,0');
    
    // Add custom filters
    if (filter.invoiceType !== undefined) {
      params.set('filter', `invoiceType,=,${filter.invoiceType}`);
    }
    
    if (filter.balanceOperator && filter.balanceValue !== undefined) {
      params.set('filter', `balance,${filter.balanceOperator},${filter.balanceValue}`);
    }
    
    // Pagination
    params.append('page', (filter.page || 0).toString());
    params.append('size', (filter.size || 10).toString());
    
    // Sorting
    params.append('sort', filter.sort || 'createdDate,desc');
    
    // Entity ID
    if (filter.entId) {
      params.append('entId', filter.entId);
    }
    
    // Add TaxToken to params if available (etax API-д ашиглах)
    if (globalToken) {
      params.append('token', globalToken);
      console.log('🔑 TaxToken added to request params');
      console.log('🔑 TaxToken length:', globalToken.length);
      console.log('🔑 TaxToken preview:', globalToken.substring(0, 50) + '...');
    } else {
      console.log('⚠️ No TaxToken available');
    }
    
    // Backend server IP address based on environment
    const BACKEND_SERVER = API_CONFIG.BASE_URL;
    
    const apiUrl = `${API_CONFIG.BASE_URL}${API_ENDPOINTS.TAX_INVOICE.NEGATIVE_BALANCE(entId || filter.entId || '')}`;
    console.log('🌐 API URL:', apiUrl);
    
    // Debug: Check headers before API call
    console.log('🔍 Headers being sent to API:');
    console.log('  - Authorization:', globalToken ? `Bearer ${globalToken.substring(0, 20)}...` : 'None');
    console.log('  - Content-Type: application/json');
    console.log('  - X-Requested-With: XMLHttpRequest');
    
    // Use direct fetch instead of api.get to avoid double URL construction
    const response = await fetch(apiUrl, {
      headers: {
        'Authorization': `Bearer ${globalToken || getLegacyToken()}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    
      if (data.success && data.data) {
        console.log('✅ Negative invoice balance fetched successfully:', data.data);
        // Transform API response to match our interface
        const transformedData: InvoiceBalanceResponse = {
          success: data.success,
          data: {
            _embedded: {
              vActInvoiceBalances: data.data._embedded?.vActInvoiceBalances || data.data.content || []
            },
            _links: data.data._links || {},
            page: data.data.page || {
              size: data.data.size || 10,
              totalElements: data.data.totalElements || 0,
              totalPages: data.data.totalPages || 0,
              number: data.data.number || 0
            },
            // Legacy fields
            content: data.data._embedded?.vActInvoiceBalances || data.data.content || [],
            totalElements: data.data.page?.totalElements || data.data.totalElements || 0,
            totalPages: data.data.page?.totalPages || data.data.totalPages || 0
          },
          message: data.message
        };
        return transformedData;
      } else {
        console.error('❌ Failed to fetch negative invoice balance:', data);
        throw new Error(data.message || 'Failed to fetch negative invoice balance');
      }
  } catch (error) {
    console.error('❌ Error fetching negative invoice balance:', error);
    
    // 401 алдааг тусгайлан боловсруулах
    if (error.response && error.response.status === 401) {
      console.log('🔐 Token хугацаа дууссан, localStorage-аас цэвэрлэж байна');
      clearEtaxToken();
      throw new Error('Token хугацаа дууссан. Дахин нэвтрэнэ үү.');
    }
    
    throw error;
  }
};

/**
 * Get invoice balance with positive or zero balance (баланс эерэг эсвэл тэг)
 * @param filter - Filter parameters
 * @returns Promise<InvoiceBalanceResponse>
 */
export const getPositiveInvoiceBalance = async (filter: InvoiceBalanceFilter = {}, entId?: string): Promise<InvoiceBalanceResponse> => {
  try {
    console.log('🔍 Fetching positive invoice balance...', filter);
    
    // Get token from localStorage if globalToken is not available
    if (!globalToken) {
      const tokenFromStorage = getLegacyToken();
      if (tokenFromStorage) {
        setGlobalToken(tokenFromStorage);
        console.log('🔑 Token loaded from localStorage');
      } else {
        console.log('⚠️ No token available in localStorage');
      }
    }
    
    const params = new URLSearchParams();
    
    // Default filters for positive balance
    params.append('filter', 'invoiceType,=,1');
    params.append('filter', 'balance,>,0');
    
    // Add custom filters
    if (filter.invoiceType !== undefined) {
      params.set('filter', `invoiceType,=,${filter.invoiceType}`);
    }
    
    if (filter.balanceOperator && filter.balanceValue !== undefined) {
      params.set('filter', `balance,${filter.balanceOperator},${filter.balanceValue}`);
    }
    
    // Pagination
    params.append('page', (filter.page || 0).toString());
    params.append('size', (filter.size || 10).toString());
    
    // Sorting
    params.append('sort', filter.sort || 'createdDate,desc');
    
    // Entity ID
    if (filter.entId) {
      params.append('entId', filter.entId);
    }
    
    // Add TaxToken to params if available (etax API-д ашиглах)
    if (globalToken) {
      params.append('token', globalToken);
      console.log('🔑 TaxToken added to request params');
      console.log('🔑 TaxToken length:', globalToken.length);
      console.log('🔑 TaxToken preview:', globalToken.substring(0, 50) + '...');
    } else {
      console.log('⚠️ No TaxToken available');
    }
    
    // Backend server IP address based on environment
    const BACKEND_SERVER = API_CONFIG.BASE_URL;
    
    // Debug: Check headers before API call
    console.log('🔍 Headers being sent to API:');
    console.log('  - Authorization:', globalToken ? `Bearer ${globalToken.substring(0, 20)}...` : 'None');
    console.log('  - Content-Type: application/json');
    console.log('  - X-Requested-With: XMLHttpRequest');
    
    const apiUrl = `${API_CONFIG.BASE_URL}${API_ENDPOINTS.TAX_INVOICE.POSITIVE_BALANCE(entId || filter.entId || '')}`;
    console.log('🌐 API URL:', apiUrl);
    
    // Use direct fetch instead of api.get to avoid double URL construction
    const response = await fetch(apiUrl, {
      headers: {
        'Authorization': `Bearer ${globalToken || getLegacyToken()}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    
    if (data.success && data.data) {
      console.log('✅ Positive invoice balance fetched successfully:', data.data);
      // Transform API response to match our interface
      const transformedData: InvoiceBalanceResponse = {
        success: data.success,
        data: {
          _embedded: {
            vActInvoiceBalances: data.data._embedded?.vActInvoiceBalances || data.data.content || []
          },
          _links: data.data._links || {},
          page: data.data.page || {
            size: data.data.size || 10,
            totalElements: data.data.totalElements || 0,
            totalPages: data.data.totalPages || 0,
            number: data.data.number || 0
          },
          // Legacy fields
          content: data.data._embedded?.vActInvoiceBalances || data.data.content || [],
          totalElements: data.data.page?.totalElements || data.data.totalElements || 0,
          totalPages: data.data.page?.totalPages || data.data.totalPages || 0
        },
        message: data.message
      };
      return transformedData;
    } else {
      console.error('❌ Failed to fetch positive invoice balance:', data);
      throw new Error(data.message || 'Failed to fetch positive invoice balance');
    }
  } catch (error) {
    console.error('❌ Error fetching positive invoice balance:', error);
    
    // 401 алдааг тусгайлан боловсруулах
    if (error.response && error.response.status === 401) {
      console.log('🔐 Token хугацаа дууссан, localStorage-аас цэвэрлэж байна');
      clearEtaxToken();
      throw new Error('Token хугацаа дууссан. Дахин нэвтрэнэ үү.');
    }
    
    throw error;
  }
};

 

